// src/sections/sectionRegistry.ts
import type { ComponentType } from "react";
import type { IconType } from "react-icons";
import {
    FiFileText,
    FiUser,
    FiCalendar,
    FiHome, FiLock
} from "react-icons/fi";
import { Laptop, HandHeart, GraduationCap, Shield, Receipt } from "lucide-react";
import { PiCheckCircle } from "react-icons/pi";
import { MdNavigateNext } from "react-icons/md";
import { Home3 } from "iconsax-reactjs";
import { BiTransfer } from "react-icons/bi";


export const SectionStatus = {
    Completed: "Completed", NotReady: "Not Ready", Schedule: "Schedule", Scheduled: "Scheduled", Start: "Start", Continue: "Continue", InProgress: "In Progress", RequestSent: "Request Sent"
} as const;
export type SectionStatus = (typeof SectionStatus)[keyof typeof SectionStatus];
export type StatusDetail = {
    id: SectionStatus;
    icon?: ComponentType<any> | IconType;
    disable: boolean;
    className?: string;
    // New property that returns the string literal of the associated SectionStatus
    literal?: SectionStatus;
}

export const SECTIONSTATUS_DETAILS: Record<SectionStatus, StatusDetail> = {
    [SectionStatus.Completed]: {
        id: SectionStatus.Completed,
        icon: PiCheckCircle,
        disable: false,
        className: "status-completed",
        literal: SectionStatus.Completed,
    },
    [SectionStatus.NotReady]: {
        id: SectionStatus.NotReady,
        icon: FiLock,
        disable: true,
        className: "status-not-ready",
        literal: SectionStatus.NotReady,
    },
    [SectionStatus.Schedule]: {
        id: SectionStatus.Schedule,
        icon: MdNavigateNext,
        disable: true,
        className: "status-schedule",
        literal: SectionStatus.Schedule,
    },
    [SectionStatus.Scheduled]: {
        id: SectionStatus.Scheduled,
        icon: FiCalendar,
        disable: false,
        className: "status-scheduled",
        literal: SectionStatus.Scheduled,
    },
    [SectionStatus.Start]: {
        id: SectionStatus.Start,
        icon: FiUser,
        disable: false,
        className: "status-start",
        literal: SectionStatus.Start,
    },
    [SectionStatus.Continue]: {
        id: SectionStatus.Continue,
        icon: FiFileText,
        disable: false,
        className: "status-continue",
        literal: SectionStatus.Continue,
    },
    // Client-derived "In Progress" acknowledgment used when a Reach-backed status still reports
    // Not Started but the user has completed pages in the workflow. Enabled (not disabled) and
    // deliberately distinct from "Scheduled" so it doesn't imply an appointment was booked.
    [SectionStatus.InProgress]: {
        id: SectionStatus.InProgress,
        icon: MdNavigateNext,
        disable: false,
        className: "status-in-progress",
        literal: SectionStatus.InProgress,
    },
    // Client-derived "Request Sent" acknowledgment used when the user has completed the booking
    // panel but Reach hasn't surfaced the appointment yet. Formatted identically to "Scheduled".
    [SectionStatus.RequestSent]: {
        id: SectionStatus.RequestSent,
        icon: FiCalendar,
        disable: false,
        className: "status-scheduled",
        literal: SectionStatus.RequestSent,
    },
}

export const SectionId = {
    Dashboard: "dashboard",
    PersonalProfile: "personalProfile",
    AdmissionsInterview: "admissionsInterview",
    EnrollmentInformation: "enrollmentInformation",
    FinancialAid: "financialAid",
    TransferCredits: "transferCredits",
    VeteranBenefits: "veteranBenefits",
    ApplicationFees: "applicationFees",
    HousingApplication: "housingApplication",
    HighSchoolTranscript: "highSchoolTranscript",
    Documents: "documents",
    TechnologyCheck: "technologyCheck",
} as const;

export type SectionId = (typeof SectionId)[keyof typeof SectionId];

// Sub-step type for dashboard sections
export type SectionStep = { id: string; title: string; path: string };

/**
 * 2) Details for each section
 */
export type SectionDetail = {
    id: SectionId;
    title: string;
    // Accept either an IconType (from react-icons) OR a React component (e.g., iconsax-reactjs' Icon)
    icon: ComponentType<any> | IconType;
    order: number;
    route?: string;
    description?: string;
    // New boolean flags configurable at initialization:
    // Indicates whether the section is enabled (defaults to true when specified)
    enabled?: boolean;
    // Indicates whether the section should be hidden in UI (defaults to false when specified)
    hidden?: boolean;
    dbBlurb?: string;
    // Use the StatusDetail object (from SECTIONSTATUS_DETAILS) or null
    status?: StatusDetail | null;
    // New: nested steps (submenu) for pages like profile, enrollment, financial, fees
    steps?: SectionStep[];

};

/**
 * 3) Single source of truth registry (map keyed by SectionId)
 *
 * Each entry may set `enabled` and `hidden` on initialization.
 * We explicitly set defaults here (enabled: true, hidden: false).
 */
export const SECTION_DETAILS: Record<SectionId, SectionDetail> = {
    [SectionId.Dashboard]: {
        id: SectionId.Dashboard,
        title: "Dashboard",
        icon: Home3,
        order: 10,
        route: "/",
        enabled: true,
        hidden: false,
    },

    [SectionId.PersonalProfile]: {
        id: SectionId.PersonalProfile,
        title: "Personal Profile",
        icon: FiUser,
        order: 20,
        route: "/dashboard/profile?step=personal",
        enabled: true,
        hidden: false,
        dbBlurb:"Update your personal information",
        status: SECTIONSTATUS_DETAILS[SectionStatus.Completed],
        steps: [
            { id: "personal", title: "Your Information", path: "/dashboard/profile?step=personal" },
            { id: "addressInfo", title: "Address Information", path: "/dashboard/profile?step=addressInfo" },
            { id: "DOBandSSN", title: "Student Details", path: "/dashboard/profile?step=studentDetails" },
            { id: "militaryInfo", title: "Military Information", path: "/dashboard/profile?step=militaryInfo" },
            { id: "secondaryEducation", title: "High School Education", path: "/dashboard/profile?step=secondaryEducation" },
            { id: "postSecondaryEducation", title: "College Education", path: "/dashboard/profile?step=postSecondaryEducation" },
            { id: "employmentInfo", title: "Employment Information", path: "/dashboard/profile?step=employmentInfo" },
        ],
    },
    [SectionId.AdmissionsInterview]: {
        id: SectionId.AdmissionsInterview,
        title: "Admissions Interview",
        icon: FiCalendar,
        order: 30,
        route: "/dashboard/admissions-interview",
        enabled: true,
        hidden: false,
        dbBlurb: "Schedule a time for your admissions interview",
        status: SECTIONSTATUS_DETAILS[SectionStatus.Schedule]
    },
    [SectionId.EnrollmentInformation]: {
        id: SectionId.EnrollmentInformation,
        title: "Enrollment Information",
        icon: FiFileText,
        order: 30,
        route: "/dashboard/program-enrollment?step=overview",
        enabled: true,
        hidden: false,
        dbBlurb:"Access after scheduling your admissions interview",
        status: SECTIONSTATUS_DETAILS[SectionStatus.NotReady],
        // NOTE: the sidebar does NOT use this list. The real steps depend on the applicant (nursing, driver's
        // license and background-check pages only apply to some programs), so ProgramEnrollmentPage publishes
        // them through EnrollmentStepsContext and the sidebar reads those. Kept for any other consumer.
        steps: [
            { id: "overview", title: "Enrollment Overview", path: "/dashboard/program-enrollment?step=overview" },
            { id: "ferpa", title: "FERPA", path: "/dashboard/program-enrollment?step=ferpa" },
            { id: "emergencyContact", title: "Emergency Contact", path: "/dashboard/program-enrollment?step=emergencyContact" },
            { id: "student-references", title: "References", path: "/dashboard/program-enrollment?step=student-references" },
        ],
    },

    [SectionId.FinancialAid]: {
        id: SectionId.FinancialAid,
        title: "Financial Assistance",
        icon: HandHeart,
        order: 40,
        route: "/dashboard/financial?step=overview",
        enabled: true,
        hidden: false,
        dbBlurb: "Explore financial assistance options",
        steps: [
            { id: "overview", title: "Financial Overview", path: "/dashboard/financial?step=overview" },
            { id: "fsa-account", title: "FSA Account", path: "/dashboard/financial?step=fsa-account" },
            { id: "fafsa", title: "FAFSA Application", path: "/dashboard/financial?step=fafsa" },
            { id: "entrance-counseling", title: "Entrance Counseling", path: "/dashboard/financial?step=entrance-counseling" },
            { id: "master-promissory", title: "Master Promissory Note", path: "/dashboard/financial?step=master-promissory" },
            { id: "review", title: "Review & Complete", path: "/dashboard/financial?step=review" },
        ],
    },

    [SectionId.TransferCredits]: {
        id: SectionId.TransferCredits,
        title: "Transfer Credits",
        icon: BiTransfer,
        order: 40,
        route: "/TransferCredits",
        enabled: true,
        hidden: false,
        dbBlurb: "Submit documents for review"
    },
    [SectionId.VeteranBenefits]: {
        id: SectionId.VeteranBenefits,
        title: "Military and Veterans",
        icon: Shield,
        order: 40,
        route: "/MilitaryAndVeterans",
        enabled: true,
        hidden: false,
        dbBlurb: "Manage your educational benefits"
    },
    [SectionId.ApplicationFees]: {
        id: SectionId.ApplicationFees,
        title: "Enrollment Fees",
        icon: Receipt,
        order: 40,
        route: "/dashboard/fees",
        enabled: true,
        hidden: false,
        dbBlurb: "Keep your enrollment on track",
        steps: [
            { id: "overview", title: "Fee Overview", path: "/dashboard/fees" },
        ],
    },
    [SectionId.HousingApplication]: {
        id: SectionId.HousingApplication,
        title: "Housing Application",
        icon: FiHome,
        order: 40,
        route: "/HousingPage",
        enabled: true,
        hidden: false,
        dbBlurb: "Apply for on-campus housing"
    },

    [SectionId.HighSchoolTranscript]: {
        id: SectionId.HighSchoolTranscript,
        title: "High School Transcript",
        icon: GraduationCap,
        order: 40,
        route: "/HighSchoolTranscript",
        enabled: true,
        hidden: false,
        dbBlurb: "Track your transcript status"
    },

    [SectionId.Documents]: {
        id: SectionId.Documents,
        title: "Documents",
        icon: FiFileText,
        order: 50,
        route: "/dashboard/documents",
        enabled: true,
        hidden: true, // TEMP: hidden until Documents feature is ready
        dbBlurb: "Manage your enrollment and school documents. ",
        status: SECTIONSTATUS_DETAILS[SectionStatus.NotReady]
    },

    [SectionId.TechnologyCheck]: {
        id: SectionId.TechnologyCheck,
        title: "Technology Check",
        icon: Laptop,
        order: 60,
        route: "/dashboard/TechnologyCheck",
        enabled: true,
        hidden: false,
        dbBlurb: "Review your PC specs" 
    },
};

/**
 * 4) Convenience exports (feels like enums for title/icon)
 */
export const SectionTitle: Record<SectionId, string> = Object.fromEntries(
    (Object.keys(SECTION_DETAILS) as SectionId[]).map((id) => [id, SECTION_DETAILS[id].title])
) as Record<SectionId, string>;

// Updated to allow either IconType or ComponentType<any>
export const SectionIcon: Record<SectionId, ComponentType<any> | IconType> = Object.fromEntries(
    (Object.keys(SECTION_DETAILS) as SectionId[]).map((id) => [id, SECTION_DETAILS[id].icon])
) as Record<SectionId, ComponentType<any> | IconType>;

/**
 * 5) Helpers
 */
export function getSectionDetail(id: SectionId): SectionDetail {
    return SECTION_DETAILS[id];
}

export function listSections(): SectionDetail[] {
    return (Object.values(SECTION_DETAILS) as SectionDetail[]).slice().sort((a, b) => a.order - b.order);
}
